/**
 * HiuConsentRecordsPage - /org/health-info/consents/:consentId/records (R191 mockup screen 4).
 * Searchable list of the records received for one consent, grouped by organisation, with
 * type chips and "show more". Opening a record shows it in place of the list.
 *
 * Requests: ONE consent GET, then ONE records GET per organisation that has records, all on
 * mount (deps: consentId only). Search / chips / show more / opening a record are client-side.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import AdminPageHeader from '../components/common/AdminPageHeader';
import AbdmEnabledGate from '../components/common/AbdmEnabledGate';
import RecordDetailBody from '../components/hiu/RecordDetailBody';
import { isServerActive, orgsOf, orgLabel, hasRecords } from '../components/hiu/consentOrgs';
import { recordDate, recordSummary } from '../components/hiu/recordRows';
import { noteStyle } from '../components/hiu/noteStyle';
import { useTheme } from '../theme/ThemeContext';
import { getConsent, getHealthInfoRecords } from '../services/api/hiuApi';
import { formatDateIST } from '../utils/formatIST';
import { ROUTES } from '../constants/routes';
import { RECORDS_VIEW, OTHER_ORGS } from '../constants/abdmCopy';
import { hiTypeLabel } from '../constants/abdmHiTypes';

const FIRST_PAGE = 5;
const PAGE = 25;

function HiuConsentRecordsPageBody() {
  const { consentId } = useParams();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [state, setState] = useState({ loading: true, error: '', consent: null, groups: [] });
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [shown, setShown] = useState({});
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const consent = await getConsent(consentId);
        if (cancelled) return;
        if (!isServerActive(consent)) {
          setState({ loading: false, error: '', consent, groups: [] });
          return;
        }
        const sources = orgsOf(consent).filter(hasRecords);
        const results = await Promise.allSettled(sources.map((a) => getHealthInfoRecords(a.latestFetchId)));
        if (cancelled) return;
        const groups = sources.map((a, i) => ({
          artefactId: a.artefactId,
          fetchId: a.latestFetchId,
          label: orgLabel(a),
          partial: a.fetchStatus === 'RECEIVED' && !!a.failureReason,
          records: results[i].status === 'fulfilled' ? results[i].value : null,
        }));
        setState({ loading: false, error: '', consent, groups });
      } catch (err) {
        if (!cancelled) setState({ loading: false, error: err.userMessage || RECORDS_VIEW.loadError, consent: null, groups: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [consentId]);

  const rows = useMemo(() => state.groups.map((g) => ({
    ...g,
    rows: (g.records || []).map((r) => ({
      record: r, fetchId: g.fetchId, label: g.label, typeLabel: hiTypeLabel(r.hiType),
      summary: recordSummary(r), date: recordDate(r),
    })).sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)),
  })), [state.groups]);

  const types = useMemo(() => {
    const t = new Set();
    rows.forEach((g) => g.rows.forEach((r) => t.add(r.record.hiType)));
    return [...t];
  }, [rows]);

  const q = query.trim().toLowerCase();
  const visibleGroups = rows.map((g) => ({
    ...g,
    rows: g.rows.filter((r) => (!typeFilter || r.record.hiType === typeFilter)
      && (!q || `${r.typeLabel} ${r.summary} ${r.label}`.toLowerCase().includes(q))),
  })).filter((g) => g.rows.length > 0 || g.records === null);
  const total = visibleGroups.reduce((n, g) => n + g.rows.length, 0);

  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';
  const textMuted = isDark ? '#9ca3af' : '#6b7280';
  const textMain = isDark ? '#f5f5f7' : '#111827';
  const border = isDark ? 'rgba(255,255,255,0.12)' : '#d1d5db';
  const cardStyle = {
    background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
    borderRadius: 12, padding: 20, marginBottom: 16,
  };
  const btn = (primary) => ({
    background: primary ? (isDark ? '#0ea5e9' : '#0369a1') : (isDark ? '#1e293b' : '#f1f5f9'),
    color: primary ? (isDark ? '#0b1220' : '#ffffff') : (isDark ? '#f1f5f9' : '#374151'),
    border: `1px solid ${primary ? 'transparent' : border}`,
    borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  });
  const chip = (on) => ({
    ...btn(on), borderRadius: 99, padding: '4px 12px', fontSize: 12,
  });
  const backToConsent = () => navigate(ROUTES.HIU_CONSENT_DETAIL.replace(':consentId', consentId));

  let body;
  if (state.loading) {
    body = <p style={{ color: textMuted }}>{RECORDS_VIEW.loading}</p>;
  } else if (state.error) {
    body = <p style={{ color: isDark ? '#f87171' : '#dc2626' }}>{state.error}</p>;
  } else if (!isServerActive(state.consent)) {
    body = <div style={noteStyle('gray', isDark)}>{RECORDS_VIEW.inactive}</div>;
  } else if (selected) {
    body = (
      <div style={cardStyle}>
        <button type="button" onClick={() => setSelected(null)} style={btn(false)}>{RECORDS_VIEW.backToRecords}</button>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: textMain, margin: '14px 0 2px' }}>{selected.typeLabel}</h2>
        <p style={{ fontSize: 13, color: textMuted, margin: '0 0 12px' }}>
          {formatDateIST(selected.date)} · {selected.label}
        </p>
        <RecordDetailBody record={selected.record} fetchId={selected.fetchId} isDark={isDark} />
      </div>
    );
  } else if (rows.length === 0) {
    body = <p style={{ color: textMuted }}>{RECORDS_VIEW.none}</p>;
  } else {
    body = (
      <>
        <p style={{ fontSize: 13, color: textMuted, margin: '0 0 12px' }}>
          {RECORDS_VIEW.fromOrgs(rows.length)}
        </p>
        <input
          type="search" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder={RECORDS_VIEW.search} aria-label={RECORDS_VIEW.search}
          style={{
            width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 8, fontSize: 14,
            border: `1px solid ${border}`, background: isDark ? '#1e293b' : '#ffffff', color: textMain, marginBottom: 10,
          }}
        />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          <button type="button" aria-pressed={!typeFilter} onClick={() => setTypeFilter('')} style={chip(!typeFilter)}>
            {RECORDS_VIEW.allTypes}
          </button>
          {types.map((t) => (
            <button key={t} type="button" aria-pressed={typeFilter === t} onClick={() => setTypeFilter(t)} style={chip(typeFilter === t)}>
              {hiTypeLabel(t)}
            </button>
          ))}
        </div>
        {total === 0 && visibleGroups.every((g) => g.records !== null) && (
          <p style={{ color: textMuted }}>{RECORDS_VIEW.noMatch}</p>
        )}
        {visibleGroups.map((g) => {
          const limit = shown[g.artefactId] || FIRST_PAGE;
          const remaining = g.rows.length - limit;
          return (
            <div key={g.artefactId} style={{ ...cardStyle, padding: 0, overflow: 'hidden' }} data-testid={`records-${g.artefactId}`}>
              <div style={{ padding: '12px 20px', background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc', fontWeight: 700, color: textMain, fontSize: 14 }}>
                {g.label} · {RECORDS_VIEW.groupCount(g.rows.length)}
              </div>
              {g.records === null && (
                <div style={{ ...noteStyle('err', isDark), margin: 12 }}>{RECORDS_VIEW.orgLoadError(g.label)}</div>
              )}
              {g.rows.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ color: textMuted, textAlign: 'left' }}>
                      <th style={{ padding: '8px 20px' }}>{RECORDS_VIEW.colRecord}</th>
                      <th style={{ padding: 8 }}>{RECORDS_VIEW.colDate}</th>
                      <th style={{ padding: 8 }}>{RECORDS_VIEW.colOrg}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.slice(0, limit).map((r) => (
                      <tr key={r.record.recordId} style={{ borderTop: `1px solid ${border}`, color: textMain }}>
                        <td style={{ padding: '8px 20px' }}>
                          <b>{r.typeLabel}</b>
                          {r.summary && <div style={{ color: textMuted }}>{r.summary}</div>}
                        </td>
                        <td style={{ padding: 8, whiteSpace: 'nowrap' }}>{formatDateIST(r.date) || '—'}</td>
                        <td style={{ padding: 8 }}>{r.label}</td>
                        <td style={{ padding: '8px 20px', textAlign: 'right' }}>
                          <button type="button" onClick={() => setSelected(r)} style={btn(false)}>{RECORDS_VIEW.view}</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {remaining > 0 && (
                <div style={{ padding: 12, textAlign: 'center' }}>
                  <button type="button" onClick={() => setShown((s) => ({ ...s, [g.artefactId]: limit + PAGE }))} style={btn(false)}>
                    {RECORDS_VIEW.showMore(remaining)}
                  </button>
                </div>
              )}
              {g.partial && <div style={{ ...noteStyle('warn', isDark), margin: 12 }}>{OTHER_ORGS.partialNote(g.label)}</div>}
            </div>
          );
        })}
      </>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: bg }}>
      <AdminPageHeader />
      <main style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
        <button type="button" onClick={backToConsent} style={{ background: 'transparent', border: 'none', color: textMuted, fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 12 }}>
          ← {RECORDS_VIEW.back}
        </button>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: textMain, margin: '0 0 4px' }}>
          {RECORDS_VIEW.title}{!state.loading && !state.error && isServerActive(state.consent) ? ` (${rows.reduce((n, g) => n + (g.records ? g.records.length : 0), 0)})` : ''}
        </h1>
        {body}
      </main>
    </div>
  );
}

export default function HiuConsentRecordsPage() {
  return (
    <AbdmEnabledGate>
      <HiuConsentRecordsPageBody />
    </AbdmEnabledGate>
  );
}
